<template>
  <div class="mx-auto mb-12 mt-10 max-w-md lg:mb-8">
    <form class="form-wrapper" @submit.prevent="onSubmit">
      <InputWrapped
        v-if="collectFields.firstName"
        :placeholder="t('input.label.firstName')"
        :label="t('input.label.firstName')"
        name="firstName"
        type="text"
        v-model="firstName"
        v-bind="firstNameAttrs"
        :error="errors.firstName"
        :disabled="isSubmitting || isLoading"
        autocomplete="given-name"
      />
      <InputWrapped
        v-if="collectFields.lastName"
        :placeholder="t('input.label.lastName')"
        :label="t('input.label.lastName')"
        name="lastName"
        type="text"
        v-model="lastName"
        v-bind="lastNameAttrs"
        :error="errors.lastName"
        :disabled="isSubmitting || isLoading"
        autocomplete="family-name"
      />
      <InputWrapped
        v-if="collectFields.email"
        :placeholder="t('input.label.email')"
        :label="t('input.label.email')"
        name="email"
        type="email"
        v-model="email"
        v-bind="emailAttrs"
        :error="errors.email"
        :disabled="isSubmitting || isLoading"
        autocomplete="username"
        :readonly="emailDisabled"
      >
        <template v-if="canChangeEmail" #append>
          <ChangeEmailDialog v-model="isEmailPopoverOpen" :disabled="isSubmitting || isLoading" />
        </template>
      </InputWrapped>
      <InputLocation
        v-if="collectFields.location"
        :placeholder="t('input.placeholder.location')"
        name="location"
        :label="t('input.label.location')"
        v-model="location"
        v-bind="locationAttrs"
        :errorInput="errors.location"
        :disabled="isSubmitting || isLoading"
      />
      <PhoneNumberWrapped
        v-if="collectFields.phoneNumber"
        :placeholder="t('input.label.phoneNumber')"
        :label="t('input.label.phoneNumber')"
        name="phoneNumber"
        v-model="phoneNumber"
        v-bind="phoneNumberAttrs"
        :error="errors.phoneNumber"
        :disabled="isSubmitting || isLoading"
      />
      <DatePickerWrapped
        v-if="collectFields.birthDate"
        :placeholder="t('input.placeholder.birthDate')"
        :label="t('input.label.birthDate')"
        name="birthDate"
        v-model="birthDate"
        v-bind="birthDateAttrs"
        :error="errors.birthDate"
        :disabled="isSubmitting || isLoading"
        :minYearsAgo="minAge"
      />
      <template v-if="collectFields.password">
        <InputWrapped
          :label="t('input.label.createPassword')"
          :placeholder="t('input.label.createPassword')"
          name="password"
          type="password"
          v-model="password"
          v-bind="passwordAttrs"
          :error="errors.password"
          :disabled="isSubmitting || isLoading"
          autocomplete="new-password"
        />
        <InputWrapped
          :label="t('input.label.retypePassword')"
          :placeholder="t('input.label.retypePassword')"
          name="retypePassword"
          type="password"
          v-model="retypePassword"
          v-bind="retypePasswordAttrs"
          :error="errors.retypePassword"
          :disabled="isSubmitting || isLoading"
          autocomplete="new-password"
        />
      </template>
      <div v-if="collectFields.consentEmail || collectFields.consentMessaging" class="grid gap-y-2 pb-2">
        <CheckboxWrapped
          v-if="collectFields.consentEmail"
          v-model="consentEmail"
          :label="consentEmailLabel"
          name="consentEmail"
          v-bind="consentEmailAttrs"
          :error="errors.consentEmail"
          :disabled="isSubmitting || isLoading"
        />
        <CheckboxWrapped
          v-if="collectFields.consentMessaging"
          v-model="consentMessaging"
          :label="consentMessagingLabel"
          name="consentMessaging"
          v-bind="consentMessagingAttrs"
          :error="errors.consentMessaging"
          :disabled="isSubmitting || isLoading"
        />
      </div>
      <Button type="submit" :disabled="isSubmitting || isLoading" :loading="isSubmitting">
        <Loader2 v-if="isSubmitting" class="mr-2 h-4 w-4 animate-spin stroke-white" />
        {{ submitButtonText || (isSubmitting ? t('common.submitting') : t('common.submit')) }}
      </Button>
      <a
        class="text-1xl link mt-4 cursor-pointer text-center hover:underline"
        :label="t('common.logout')"
        @click="accountStore.logoutFan(true)"
        @keydown.enter="accountStore.logoutFan(true)"
      >
        {{ t('common.logout') }}
      </a>
      <InfoBar
        v-if="error"
        variant="destructive"
        :title="t('common.error')"
        :message="error"
        @close="emit('clearError')"
      />
      <InfoBar
        v-if="success"
        variant="success"
        :title="t('common.success')"
        :message="success"
        @close="emit('clearSuccess')"
      />
    </form>
    <slot name="form-footer" />
  </div>
</template>

<script setup lang="ts">
import { computed, watch, ref } from 'vue';
import { useForm } from 'vee-validate';
import { useArtistStore } from '@stores/artist.store';
import { useAccountStore } from '@stores/account.store';
import { toTypedSchema } from '@vee-validate/yup';
import * as yup from 'yup';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import {
  DefaultUserDetails,
  DefaultUserDetailsForm,
  type UserDetails,
  type UserDetailsFormProps,
} from './UserDetails.interface';

import { Loader2 } from 'lucide-vue-next';
import Button from '@ui/button/Button.vue';
import InputWrapped from '@modules/InputWrapped/InputWrapped.vue';
import CheckboxWrapped from '@modules/CheckboxWrapped/CheckboxWrapped.vue';
import DatePickerWrapped from '@modules/DatePickerWrapped/DatePickerWrapped.vue';
import InputLocation from '@modules/InputLocation/InputLocation.vue';
import PhoneNumberWrapped from '@modules/PhoneNumberWrapped/PhoneNumberWrapped.vue';
import InfoBar from '@generics/InfoBar.vue';
import ChangeEmailDialog from '@modules/ChangeEmailDialog/ChangeEmailDialog.vue';
import moment from 'moment';

const { t } = useI18n();

const props = withDefaults(defineProps<UserDetailsFormProps>(), DefaultUserDetailsForm);
const emit = defineEmits(['submit', 'change', 'clearError', 'clearSuccess']);

const artistStore = useArtistStore();
const accountStore = useAccountStore();
const { name: artistName, minAge } = storeToRefs(artistStore);
const consentEmailLabel = computed(() => t('dynamic.termsEmail', { artistName: artistName.value }));
const consentMessagingLabel = computed(() => t('dynamic.termsPhone', { artistName: artistName.value }));
const isEmailPopoverOpen = ref(false);

const createValidationSchema = () => {
  const schemaObj: Record<string, yup.AnySchema> = {};

  if (props.requiredRules.firstName) {
    schemaObj.firstName = yup.string().required(t('errors.required'));
  }
  if (props.requiredRules.lastName) {
    schemaObj.lastName = yup.string().required(t('errors.required'));
  }
  if (props.requiredRules.email) {
    schemaObj.email = yup.string().required(t('errors.required')).email(t('errors.invalidEmail'));
  }
  if (props.requiredRules.phoneNumber) {
    schemaObj.phoneNumber = yup
      .string()
      .required(t('errors.required'))
      .matches(/^\+[1-9]\d{1,14}$/, t('errors.invalidPhoneNumber'));
  }
  if (props.requiredRules.location) {
    schemaObj.location = yup.object().required(t('errors.required'));
  }
  if (props.requiredRules.birthDate) {
    schemaObj.birthDate = yup
      .string()
      .required(t('errors.required'))
      .test('maxAge', t('dynamic.minAgeNotMet', { minAge: minAge.value }), (value) => {
        if (!value) return true;
        const selectedDate = moment(value).startOf('day');
        const maxDate = moment().endOf('day').subtract(minAge.value, 'years');
        return selectedDate.isSameOrBefore(maxDate);
      });
  }
  if (props.requiredRules.password) {
    schemaObj.password = yup
      .string()
      .required(t('errors.required'))
      .min(8, t('errors.passwordMinLengthNotMet'));
    schemaObj.retypePassword = yup
      .string()
      .required(t('errors.required'))
      .oneOf([yup.ref('password')], t('errors.passwordsMustMatch'));
  }
  if (props.requiredRules.consentEmail) {
    schemaObj.consentEmail = yup.boolean().isTrue(t('errors.required'));
  }
  if (props.requiredRules.consentMessaging) {
    schemaObj.consentMessaging = yup.boolean().isTrue(t('errors.required'));
  }

  return yup.object(schemaObj);
};

const schema = computed(() => {
  return toTypedSchema(createValidationSchema());
});

const { errors, handleSubmit, controlledValues, defineField, values, setValues } =
  useForm<UserDetails>({
    validationSchema: schema,
    validateOnMount: false,
    initialValues: { ...DefaultUserDetails, ...props.initialValues },
  });

const [firstName, firstNameAttrs] = defineField('firstName');
const [lastName, lastNameAttrs] = defineField('lastName');
const [email, emailAttrs] = defineField('email');
const [phoneNumber, phoneNumberAttrs] = defineField('phoneNumber');
const [location, locationAttrs] = defineField('location');
const [birthDate, birthDateAttrs] = defineField('birthDate');
const [password, passwordAttrs] = defineField('password');
const [retypePassword, retypePasswordAttrs] = defineField('retypePassword');
const [consentEmail, consentEmailAttrs] = defineField('consentEmail');
const [consentMessaging, consentMessagingAttrs] = defineField('consentMessaging');

const onSubmit = handleSubmit(() => {
  emit('submit', controlledValues.value);
});

watch(
  () => props.initialValues,
  (newValue) => {
    if (newValue) setValues(newValue);
  },
);

watch(values, () => emit('change'), { deep: true });
</script>
